import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import * as echarts from 'echarts';
import type { Task, VehicleNodes, TaskStatus } from '@/types';
import { AlertCircle, Paperclip } from 'lucide-react';
import { stitchImages, downloadCanvas, loadImage } from '@/utils/imageStitch';
import { Loader } from '@/components/ui';

interface GanttChartProps {
  tasks: Task[];
  vehicleNodes?: VehicleNodes;
  height?: number;
  onTaskClick?: (task: Task) => void;
  setIsExporting?: (isExporting: boolean) => void;
}

export interface GanttChartRef {
  exportToPNG: (filename: string) => void;
  scrollToTask: (taskId: string) => void;
}

// 任务状态颜色
const statusColors: Record<TaskStatus, string> = {
  'not-started': '#9ca3af',     // 灰色
  'in-progress': '#2a36e2',      // 蓝色
  'completed': '#57e434',        // 绿色
  'delayed': '#f44245',          // 红色
  'delayed-completed': '#f59e0b', // 橙色
};

const phaseColors: Record<string, string> = {
  supplierSelection: '#2a36e2',
  stylingConcept: '#8b5cf6',
  detailedDesign: '#06b6d4',
  toolingLine: '#f59e0b',
  productDevelopment: '#57e434',
};

const phaseNames: Record<string, string> = {
  supplierSelection: 'Supplier Selection',
  stylingConcept: 'Styling Concept',
  detailedDesign: 'Engineering Design',
  toolingLine: 'Tooling & Line',
  productDevelopment: 'Product Development',
};

const phaseOrder = ['supplierSelection', 'stylingConcept', 'detailedDesign', 'toolingLine', 'productDevelopment'];

export const GanttChart = forwardRef<GanttChartRef, GanttChartProps>(
  ({ tasks, vehicleNodes, height = 500, onTaskClick, setIsExporting }, ref) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);
    const dataZoomStateRef = useRef<{ start: number; end: number } | null>(null);
    const todayTimeRef = useRef<number>(new Date().getTime());
    const todayMarkerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef<{ x: number; y: number; xStart: number; xEnd: number; yStartValue: number; yEndValue: number } | null>(null);

    const validTasks = tasks.filter(t => t && t.name && t.start && t.end);

    useImperativeHandle(ref, () => ({
      exportToPNG: async (filename: string) => {
        if (!chartInstance.current || !chartRef.current) return;

        // 通知父组件显示导出 loader
        if (setIsExporting) {
          setIsExporting(true);
        }

        try {
          const currentOption = chartInstance.current.getOption();
          const originalDataZoom = (currentOption?.dataZoom || []) as any[];
          const originalXZoom = originalDataZoom[0] || {};
          const originalYZoom = originalDataZoom[1] || {};

          const seriesData = (currentOption?.series?.[0] as any)?.data || [];
          const totalTasks = seriesData.length;

          const xAxisMin = (currentOption?.xAxis?.[0] as any)?.min || 0;
          const xAxisMax = (currentOption?.xAxis?.[0] as any)?.max || 0;
          const timeSpan = xAxisMax - xAxisMin;
          const weeks = Math.ceil(timeSpan / (7 * 24 * 60 * 60 * 1000));

          const PIXELS_PER_WEEK = 80;
          const PIXELS_PER_TASK = 30;
          const LEFT_MARGIN = 180;
          const RIGHT_MARGIN = 40;
          const TOP_MARGIN = 20;
          const BOTTOM_MARGIN = 80;

          const chartWidth = LEFT_MARGIN + RIGHT_MARGIN + weeks * PIXELS_PER_WEEK;
          const chartHeight = TOP_MARGIN + BOTTOM_MARGIN + totalTasks * PIXELS_PER_TASK;

          const originalWidth = chartRef.current.clientWidth;
          const originalHeight = chartRef.current.clientHeight;

          chartRef.current.style.width = `${chartWidth}px`;
          chartRef.current.style.height = `${chartHeight}px`;

          await new Promise(resolve => setTimeout(resolve, 100));

          chartInstance.current.resize({
            width: chartWidth,
            height: chartHeight,
            animation: {
              duration: 0
            }
          });

          chartInstance.current.setOption({
            dataZoom: [
              {
                type: 'slider',
                xAxisIndex: 0,
                start: 0,
                end: 100,
                minValueSpan: 7 * 24 * 60 * 60 * 1000
              },
              {
                type: 'slider',
                yAxisIndex: 0,
                startValue: 0,
                endValue: Math.max(0, totalTasks - 1)
              }
            ]
          });

          await new Promise(resolve => setTimeout(resolve, 800));

          const dataURL = chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          });

          const link = document.createElement('a');
          link.download = filename;
          link.href = dataURL;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          chartRef.current.style.width = `${originalWidth}px`;
          chartRef.current.style.height = `${originalHeight}px`;

          chartInstance.current.resize({
            width: originalWidth,
            height: originalHeight,
            animation: {
              duration: 0
            }
          });

          chartInstance.current.setOption({
            dataZoom: [
              {
                type: 'slider',
                start: originalXZoom.start ?? 0,
                end: originalXZoom.end ?? 100
              },
              {
                type: 'slider',
                startValue: originalYZoom.startValue ?? 0,
                endValue: originalYZoom.endValue ?? totalTasks - 1
              }
            ]
          });
        } finally {
          // 通知父组件隐藏导出 loader
          if (setIsExporting) {
            setIsExporting(false);
          }
        }
      },
      scrollToTask: (taskIdentifier: string) => {
        if (!chartInstance.current || !taskIdentifier) return;
        
        const option = chartInstance.current.getOption();
        const series = option?.series?.[0];
        const data = series?.data || [];
        
        let taskIndex = data.findIndex((item: { taskId: string }) => 
          item.taskId === taskIdentifier
        );
        
        if (taskIndex === -1) {
          taskIndex = data.findIndex((item: { name: string }) => 
            item.name.toLowerCase().includes(taskIdentifier.toLowerCase())
          );
        }
        
        if (taskIndex !== -1) {
          const taskData = data[taskIndex];
          const taskStartTime = taskData?.value?.[0];
          const taskEndTime = taskData?.value?.[0] + taskData?.value?.[2];
          const xAxisMin = option?.xAxis?.[0]?.min;
          const xAxisMax = option?.xAxis?.[0]?.max;
          
          chartInstance.current.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: taskIndex
          });
          
          chartInstance.current.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: taskIndex
          });
          
          if (taskStartTime !== undefined && taskEndTime !== undefined && xAxisMin !== undefined && xAxisMax !== undefined) {
            const taskCenter = (taskStartTime + taskEndTime) / 2;
            const timeRange = xAxisMax - xAxisMin;
            const zoomRange = timeRange * 0.3;
            const newMinX = taskCenter - zoomRange / 2;
            const newMaxX = taskCenter + zoomRange / 2;
            
            const xStartPercent = ((newMinX - xAxisMin) / timeRange) * 100;
            const xEndPercent = ((newMaxX - xAxisMin) / timeRange) * 100;
            
            const totalTasks = data.length;
            const visibleCount = 20;
            const startYIndex = Math.max(0, taskIndex - Math.floor(visibleCount / 2));
            const endYIndex = Math.min(totalTasks - 1, startYIndex + visibleCount - 1);
            
            chartInstance.current?.setOption({
              dataZoom: [
                {
                  type: 'slider',
                  xAxisIndex: 0,
                  start: Math.max(0, xStartPercent),
                  end: Math.min(100, xEndPercent)
                },
                {
                  type: 'slider',
                  yAxisIndex: 0,
                  startValue: startYIndex,
                  endValue: endYIndex
                }
              ]
            });
            
            setTimeout(() => {
              const todayPoint = chartInstance.current?.convertToPixel({ xAxisIndex: 0 }, todayTimeRef.current);
              if (todayPoint && typeof todayPoint === 'number' && todayMarkerRef.current) {
                const chartWidth = chartRef.current?.clientWidth || 800;
                const leftMargin = 180;
                const rightMargin = 40;
                
                if (todayPoint >= leftMargin && todayPoint <= chartWidth - rightMargin) {
                  todayMarkerRef.current.style.display = 'block';
                  todayMarkerRef.current.style.left = `${todayPoint}px`;
                } else {
                  todayMarkerRef.current.style.display = 'none';
                }
              }
            }, 150);
          }
        }
      }
    }));

    useEffect(() => {
      if (!chartRef.current) return;

      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      const handleResize = () => {
        chartInstance.current?.resize();
      };
      window.addEventListener('resize', handleResize);

      setTimeout(() => {
        chartInstance.current?.resize();
      }, 100);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      const chartDom = chartRef.current;
      if (!chartDom || !chartInstance.current) return;

      const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('.echarts-data-zoom') || target.closest('canvas')) {
          return;
        }
        
        const option = chartInstance.current?.getOption();
        if (!option?.dataZoom) return;
        
        const xZoom = (option.dataZoom as any[]).find((z: any) => z.type === 'slider' && z.xAxisIndex === 0);
        const yZoom = (option.dataZoom as any[]).find((z: any) => z.type === 'slider' && z.yAxisIndex === 0);
        
        if (xZoom && yZoom) {
          dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            xStart: xZoom.start || 0,
            xEnd: xZoom.end || 100,
            yStartValue: yZoom.startValue ?? 0,
            yEndValue: yZoom.endValue ?? 19
          };
          setIsDragging(true);
        }
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || !dragStartRef.current || !chartInstance.current) return;

        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        
        const chartWidth = chartDom.clientWidth - 180 - 40;
        const chartHeight = chartDom.clientHeight - 20 - 80;
        
        const option = chartInstance.current.getOption();
        const seriesData = (option?.series?.[0] as any)?.data || [];
        const totalTasks = seriesData.length;
        
        const xRange = dragStartRef.current.xEnd - dragStartRef.current.xStart;
        const yRange = dragStartRef.current.yEndValue - dragStartRef.current.yStartValue;
        
        const deltaXPercent = (deltaX / chartWidth) * xRange;
        const deltaYTasks = Math.round((deltaY / chartHeight) * yRange);
        
        let newXStart = dragStartRef.current.xStart - deltaXPercent;
        let newXEnd = dragStartRef.current.xEnd - deltaXPercent;
        
        if (newXStart < 0) {
          newXStart = 0;
          newXEnd = xRange;
        }
        if (newXEnd > 100) {
          newXEnd = 100;
          newXStart = 100 - xRange;
        }
        
        let newYStartValue = dragStartRef.current.yStartValue + deltaYTasks;
        let newYEndValue = dragStartRef.current.yEndValue + deltaYTasks;
        
        if (newYStartValue < 0) {
          newYStartValue = 0;
          newYEndValue = yRange;
        }
        if (newYEndValue > totalTasks - 1) {
          newYEndValue = totalTasks - 1;
          newYStartValue = Math.max(0, totalTasks - 1 - yRange);
        }

        chartInstance.current.setOption({
          dataZoom: [
            { type: 'slider', xAxisIndex: 0, start: newXStart, end: newXEnd },
            { type: 'inside', xAxisIndex: 0, start: newXStart, end: newXEnd },
            { type: 'slider', yAxisIndex: 0, startValue: newYStartValue, endValue: newYEndValue },
            { type: 'inside', yAxisIndex: 0, startValue: newYStartValue, endValue: newYEndValue }
          ]
        });
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        dragStartRef.current = null;
      };

      chartDom.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        chartDom.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isDragging]);

    useEffect(() => {
      if (!chartRef.current) return;
      
      if (validTasks.length === 0) {
        if (chartInstance.current) {
          chartInstance.current.clear();
        }
        return;
      }
      
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      const seriesData: any[] = [];
      const yAxisData: any[] = [];
      const now = new Date();
      todayTimeRef.current = now.getTime();
      // 使用相同的时间戳创建日期字符串，确保一致性
      const todayStr = now.toISOString();
      
      let minTime = Infinity;
      let maxTime = -Infinity;
      let rowIndex = 0;

      // 按 subTask 分组任务，用于绘制连接线
      const subTaskGroups: Record<string, { task: any; rowIndex: number; startTime: number; endTime: number }[]> = {};

      phaseOrder.forEach((phaseKey) => {
        const phaseTasks = validTasks.filter(t => t.phase === phaseKey);
        
        phaseTasks.forEach((task, taskIndex) => {
          const startTime = new Date(task.start).getTime();
          const endTime = new Date(task.end).getTime();
          
          if (isNaN(startTime) || isNaN(endTime)) return;
          
          minTime = Math.min(minTime, startTime);
          maxTime = Math.max(maxTime, endTime);
          
          const duration = endTime - startTime;
          const taskColor = statusColors[task.status] || phaseColors[task.phase] || '#9ca3af';
          
          yAxisData.push({
            value: rowIndex,
            name: task.name,
            subTask: task.subTask || '',
            phase: phaseNames[phaseKey],
            phaseKey: phaseKey,
            isFirstInPhase: taskIndex === 0
          });
          
          const taskData = {
            name: task.name,
            value: [startTime, rowIndex, duration, task.end, taskColor],
            taskId: task.id,
            task: task,
            phase: phaseKey,
            subTask: task.subTask || '',
            reviewTask: task.reviewTask || false,
            reviewAttachments: task.reviewAttachments || []
          };
          
          seriesData.push(taskData);
          
          // 按 subTask 分组存储任务信息
          if (task.subTask) {
            if (!subTaskGroups[task.subTask]) {
              subTaskGroups[task.subTask] = [];
            }
            subTaskGroups[task.subTask].push({
              task: taskData,
              rowIndex: rowIndex,
              startTime: startTime,
              endTime: endTime
            });
          }
          
          rowIndex++;
        });
      });
      
      if (minTime === Infinity) {
        minTime = new Date().getTime();
        maxTime = minTime + 365 * 24 * 60 * 60 * 1000;
      }
      
      const timeRange = maxTime - minTime;
      minTime = minTime - timeRange * 0.02;
      maxTime = maxTime + timeRange * 0.02;

      // 时间常量
      const ONE_DAY = 24 * 60 * 60 * 1000;
      const ONE_WEEK = 7 * ONE_DAY;
      const TWO_WEEKS = 14 * ONE_DAY;
      const ONE_MONTH = 30 * ONE_DAY;

      // 固定使用周为间隔
      const calculateTickInterval = (_range: number): number => {
        return ONE_WEEK;
      };

      // 生成刻度点
      const generateTickPoints = (start: number, end: number, interval: number): number[] => {
        const points: number[] = [];
        const startDate = new Date(start);
        startDate.setHours(0, 0, 0, 0);
        let current = startDate.getTime();
        
        while (current <= end + interval) {
          points.push(current);
          current += interval;
        }
        return points;
      };

      // 初始刻度间隔
      const initialInterval = calculateTickInterval(maxTime - minTime);
      
      // 生成所有刻度点
      const tickPoints = generateTickPoints(minTime, maxTime, initialInterval);

      // 生成网格线 markLine（带日期标签）
      const generateGridMarkLines = (points: number[]) => {
        return points
          .filter(tick => tick >= minTime - ONE_DAY && tick <= maxTime + ONE_DAY)
          .map(tick => {
            const date = new Date(tick);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return {
              xAxis: tick,
              label: {
                formatter: `${month}/${day}`,
                position: 'end',
                color: '#607d8b',
                fontSize: 10,
                fontWeight: 'bold',
                distance: 8,
                rotate: 0
              },
              lineStyle: {
                color: 'rgba(0, 0, 0, 0.06)',
                type: 'solid',
                width: 1
              }
            };
          });
      };

      const gridMarkLines = generateGridMarkLines(tickPoints);

      const markLines: any[] = [...gridMarkLines];
      if (vehicleNodes) {
        Object.entries(vehicleNodes).forEach(([key, date]) => {
          if (date) {
            markLines.push({
              xAxis: date,
              label: {
                formatter: key,
                position: 'insideEndTop',
                color: '#666666',
                fontWeight: 'bold',
                fontSize: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: [2, 6],
                borderRadius: 99,
                rotate: 0
              },
              lineStyle: {
                color: '#999999',
                type: 'dashed',
                width: 2
              }
            });
          }
        });
      }

      markLines.push({
        xAxis: todayStr,
        label: {
          show: false
        },
        lineStyle: {
          color: '#f44245',
          type: 'solid',
          width: 2
        }
      });

      const option: echarts.EChartsOption = {
        backgroundColor: '#fff',
        animation: true,
        animationDuration: 800,
        animationEasing: 'cubicOut',
        animationDelay: (idx: number) => idx * 30,
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: [12, 16],
          textStyle: {
            color: '#374151',
            fontSize: 12
          },
          formatter: (params: any) => {
            const task = validTasks.find(t => t.id === params.data.taskId);
            if (!task) return params.name;
            
            const weeks = Math.ceil(task.duration / 7);
            
            const hasReviewAttachments = task.reviewAttachments && task.reviewAttachments.length > 0;
            const reviewTaskHtml = task.reviewTask ? `
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <div style="font-size: 12px; color: #374151;">
                  ${hasReviewAttachments 
                    ? '✅ Review File: Uploaded' 
                    : '⚠️ Review File: Not Uploaded'}
                </div>
              </div>
            ` : '';
            
            return `
              <div style="padding: 4px;">
                <div style="font-weight: 600; margin-bottom: 6px; font-size: 14px;">${task.name}</div>
                <div style="font-size: 12px; color: #374151; line-height: 1.6;">
                  <div>📅 Start: <strong>${task.start}</strong></div>
                  <div>📅 End: <strong>${task.end}</strong></div>
                  <div>⏱️ Duration: <strong>${weeks} weeks</strong> (${task.duration} days)</div>
                </div>
                ${reviewTaskHtml}
              </div>
            `;
          }
        },
        grid: {
          left: '40px',
          right: '40px',
          top: '20px',
          bottom: '80px'
        },
        xAxis: {
          type: 'time',
          min: minTime,
          max: maxTime,
          position: 'bottom',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            show: false
          },
          splitLine: {
            show: false
          }
        },
        yAxis: {
          type: 'category',
          data: yAxisData.map((item, index) => ({
            value: index,
            name: item.name
          })),
          inverse: true,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            show: false
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: 'rgba(0, 0, 0, 0.04)',
              type: 'solid'
            }
          }
        },
        dataZoom: [
          {
            type: 'slider',
            xAxisIndex: 0,
            filterMode: 'none',
            height: 12,
            bottom: 8,
            start: 0,
            end: 23,
            minValueSpan: 7 * 24 * 60 * 60 * 1000,
            borderColor: '#e5e7eb',
            backgroundColor: '#f9fafb',
            fillerColor: 'rgba(59, 130, 246, 0.15)',
            handleStyle: {
              color: '#2a36e2',
              borderColor: '#2a36e2'
            },
            textStyle: {
              color: '#6b7280',
              fontSize: 10
            },
            rotate: 0,
            interval: 0,
            labelFormatter: (value: number) => {
              const date = new Date(value);
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${month}/${day}`;
            }
          },
          {
            type: 'inside',
            xAxisIndex: 0,
            filterMode: 'none',
            minValueSpan: 7 * 24 * 60 * 60 * 1000,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: false,
            preventDefaultMouseMove: false
          },
          {
            type: 'slider',
            yAxisIndex: 0,
            filterMode: 'none',
            width: 12,
            right: 20,
            startValue: 0,
            endValue: Math.min(19, yAxisData.length - 1),
            minValueSpan: 5,
            borderColor: '#e5e7eb',
            backgroundColor: '#f9fafb',
            fillerColor: 'rgba(59, 130, 246, 0.15)',
            handleStyle: {
              color: '#2a36e2',
              borderColor: '#2a36e2'
            },
            showDetail: false
          },
          {
            type: 'inside',
            yAxisIndex: 0,
            filterMode: 'none',
            minValueSpan: 5,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: false,
            preventDefaultMouseMove: false
          }
        ],
        series: [
          {
            type: 'custom',
            clip: true,
            renderItem: (params: any, api: any) => {
              const categoryIndex = api.value(1);
              const start = api.coord([api.value(0), categoryIndex]);
              const end = api.coord([api.value(0) + api.value(2), categoryIndex]);
              const barHeight = 22;
              
              const rectShape = echarts.graphic.clipRectByRect({
                x: start[0],
                y: start[1] - barHeight / 2,
                width: Math.max(end[0] - start[0], 4),
                height: barHeight
              }, {
                x: params.coordSys.x,
                y: params.coordSys.y,
                width: params.coordSys.width,
                height: params.coordSys.height
              });

              const color = api.value(4) || '#2a36e2';
              const dataItem = seriesData[categoryIndex];
              const subTaskName = dataItem?.subTask || '';
              const taskName = dataItem?.name || '';
              const barWidth = rectShape?.width || 0;
              const taskStatus = dataItem?.task?.status as TaskStatus;
              const taskStartTime = dataItem?.task?.start ? new Date(dataItem.task.start).getTime() : 0;
              const taskEndTime = dataItem?.task?.end ? new Date(dataItem.task.end).getTime() : 0;
              const todayTime = todayTimeRef.current;

              const children: any[] = [];
              
              // 检查任务是否跨越今天且状态为进行中
              const isInProgressSpanningToday = taskStatus === 'in-progress' &&
                                                 taskStartTime < todayTime &&
                                                 taskEndTime > todayTime;

              // 计算今天前后两段的宽度（使用原始坐标，不是裁剪后的）
              let beforeTodayWidth = 0;
              let afterTodayWidth = 0;
              let todayX = 0;
              let taskBarY = start[1] - barHeight / 2; // 使用原始的 y 坐标

              if (isInProgressSpanningToday) {
                const taskStartX = start[0];
                const taskEndX = end[0];
                const todayPoint = api.coord([todayTime, categoryIndex]);
                todayX = todayPoint[0];
                beforeTodayWidth = Math.max(0, todayX - taskStartX);
                afterTodayWidth = Math.max(0, taskEndX - todayX);
              } else if (taskStatus === 'delayed' && todayTime > taskEndTime) {
                // 对于 delayed 任务，计算今天的 X 坐标用于延长柱状图
                const todayPoint = api.coord([todayTime, categoryIndex]);
                todayX = todayPoint[0];
              } else if (taskStatus === 'delayed-completed' && dataItem?.task?.actualCompletionDate) {
                // 对于 delayed-completed 任务，计算实际完成日期的 X 坐标
                const acdTime = new Date(dataItem.task.actualCompletionDate).getTime();
                const acdPoint = api.coord([acdTime, categoryIndex]);
                todayX = acdPoint[0];
              }
              
              // 绘制连接线（在同一 subTask 组内的任务之间）
              if (subTaskName && subTaskGroups[subTaskName]) {
                const group = subTaskGroups[subTaskName];
                const currentIndex = group.findIndex(item => item.rowIndex === categoryIndex);
                
                // 如果当前任务不是组内的最后一个任务，则绘制到下一个任务的连接线
                if (currentIndex >= 0 && currentIndex < group.length - 1) {
                  const currentTask = group[currentIndex];
                  const nextTask = group[currentIndex + 1];
                  
                  // 当前任务的结束位置 - 使用正确的坐标转换
                  const currentEndCoord = api.coord([currentTask.endTime, currentTask.rowIndex]);
                  // 下一个任务的开始位置
                  const nextStartCoord = api.coord([nextTask.startTime, nextTask.rowIndex]);
                  
                  // 检查坐标是否有效
                  if (currentEndCoord && nextStartCoord && 
                      !isNaN(currentEndCoord[0]) && !isNaN(currentEndCoord[1]) &&
                      !isNaN(nextStartCoord[0]) && !isNaN(nextStartCoord[1])) {
                    
                    const startX = currentEndCoord[0];
                    const startY = currentEndCoord[1];
                    const endX = nextStartCoord[0];
                    const endY = nextStartCoord[1];
                    
                    // 构建连接线路径 - 统一使用 L 形连线
                    let pathPoints: number[][];
                    let lineColor = '#64748b'; // 灰色
                    let lineWidth = 2;
                    
                    // 计算当前任务的开始位置
                    const currentStartCoord = api.coord([currentTask.startTime, currentTask.rowIndex]);
                    const currentStartX = currentStartCoord ? currentStartCoord[0] : startX;
                    const currentMidX = (currentStartX + startX) / 2;
                    
                    // 确保 adjustedMidX 不会和 endX 太接近
                    let adjustedMidX = currentMidX;
                    if (Math.abs(currentMidX - endX) < 30) {
                      adjustedMidX = endX - 30;
                    }
                    if (adjustedMidX < currentStartX) {
                      adjustedMidX = currentStartX + 10;
                    }
                    
                    // L 形连线
                    pathPoints = [
                      [adjustedMidX, startY],
                      [adjustedMidX, endY],
                      [endX, endY]
                    ];
                    
                    if (pathPoints.length > 0) {
                      // 绘制连接线 - 使用更高的 zlevel 确保在任务柱状图之上
                      children.push({
                        type: 'polyline',
                        shape: {
                          points: pathPoints
                        },
                        style: {
                          stroke: lineColor,
                          lineWidth: lineWidth,
                          fill: 'none',
                          lineCap: 'round',
                          lineJoin: 'round',
                          opacity: 0.9
                        },
                        z: 100,
                        zlevel: 10,
                        silent: true
                      });
                      
                      // 在起点添加小圆点
                      children.push({
                        type: 'circle',
                        shape: {
                          cx: pathPoints[0][0],
                          cy: pathPoints[0][1],
                          r: 4
                        },
                        style: {
                          fill: lineColor,
                          stroke: '#fff',
                          lineWidth: 1.5,
                          opacity: 0.9
                        },
                        z: 101,
                        zlevel: 10,
                        silent: true
                      });
                      
                      // 在终点添加小圆点
                      children.push({
                        type: 'circle',
                        shape: {
                          cx: pathPoints[pathPoints.length - 1][0],
                          cy: pathPoints[pathPoints.length - 1][1],
                          r: 4
                        },
                        style: {
                          fill: lineColor,
                          stroke: '#fff',
                          lineWidth: 1.5,
                          opacity: 0.9
                        },
                        z: 101,
                        zlevel: 10,
                        silent: true
                      });
                    }
                  }
                }
              }
              
              if (rectShape) {
                // 对于跨越今天的进行中任务，绘制两段
                if (isInProgressSpanningToday && beforeTodayWidth > 0 && afterTodayWidth > 0) {
                  // 今天前的部分 - 实心蓝色（使用原始坐标 start[0] 而不是 rectShape.x）
                  children.push({
                    type: 'rect',
                    transition: ['shape', 'style'],
                    shape: {
                      x: start[0],
                      y: taskBarY,
                      width: beforeTodayWidth,
                      height: barHeight,
                      r: 4
                    },
                    style: {
                      fill: color,
                      shadowBlur: 4,
                      shadowColor: 'rgba(0, 0, 0, 0.15)',
                      shadowOffsetX: 0,
                      shadowOffsetY: 2
                    },
                    emphasis: {
                      style: {
                        shadowBlur: 8,
                        shadowColor: 'rgba(0, 0, 0, 0.25)'
                      }
                    },
                    z: 10
                  });

                  // 今天后的部分 - 斜条纹设计
                  // 先绘制半透明背景
                  children.push({
                    type: 'rect',
                    shape: {
                      x: todayX,
                      y: taskBarY,
                      width: afterTodayWidth,
                      height: barHeight,
                      r: 4
                    },
                    style: {
                      fill: 'rgba(42, 54, 226, 0.15)'
                    },
                    z: 9
                  });

                  // 直接在半透明背景上绘制斜条纹
                  const stripeWidth = 4;
                  const stripeGap = 4;
                  const totalStripes = Math.ceil(afterTodayWidth / (stripeWidth + stripeGap)) + 1;
                  
                  for (let i = 0; i < totalStripes; i++) {
                    const offsetX = i * (stripeWidth + stripeGap);
                    children.push({
                      type: 'line',
                      shape: {
                        x1: todayX + offsetX,
                        y1: taskBarY,
                        x2: todayX + offsetX + stripeWidth,
                        y2: taskBarY + barHeight
                      },
                      style: {
                        stroke: 'rgba(255, 255, 255, 0.5)',
                        lineWidth: 2,
                        lineCap: 'round'
                      },
                      z: 10
                    });
                  }

                  // 添加一个整体的边框矩形（无填充，只有边框）
                  children.push({
                    type: 'rect',
                    shape: {
                      x: start[0],
                      y: taskBarY,
                      width: end[0] - start[0],
                      height: barHeight,
                      r: 4
                    },
                    style: {
                      fill: 'transparent',
                      stroke: color,
                      lineWidth: 1
                    },
                    z: 12
                  });
                } else {
                  // 普通任务 - 单色矩形
                  children.push({
                    type: 'rect',
                    transition: ['shape', 'style'],
                    shape: {
                      x: rectShape.x,
                      y: rectShape.y,
                      width: rectShape.width,
                      height: rectShape.height,
                      r: 4
                    },
                    style: {
                      fill: color,
                      shadowBlur: 4,
                      shadowColor: 'rgba(0, 0, 0, 0.15)',
                      shadowOffsetX: 0,
                      shadowOffsetY: 2
                    },
                    emphasis: {
                      style: {
                        shadowBlur: 8,
                        shadowColor: 'rgba(0, 0, 0, 0.25)'
                      }
                    },
                    z: 10
                  });
                }

                // 显示任务名称，根据柱状图宽度调整字体大小
                const displayText = subTaskName ? `${subTaskName} - ${taskName}` : taskName;
                const fontSize = barWidth > 40 ? 10 : 8;
                const padding = barWidth > 40 ? 8 : 4;

                // 对于分段显示的任务，将文本放在今天前的部分
                if (isInProgressSpanningToday && beforeTodayWidth > 0) {
                  children.push({
                    type: 'text',
                    style: {
                      x: start[0] + padding,
                      y: taskBarY + barHeight / 2,
                      text: displayText,
                      fill: '#fff',
                      fontSize: fontSize,
                      fontWeight: '500',
                      textVerticalAlign: 'middle',
                      overflow: 'truncate',
                      width: Math.max(beforeTodayWidth - padding * 2, 1)
                    },
                    z: 11
                  });

                  // 状态图标放在今天前的部分末端
                  if (taskStatus && taskStatus !== 'not-started') {
                    const iconConfig = {
                      'completed': { symbol: '✔', color: '#57e434' },
                      'in-progress': { symbol: '🕐', color: '#2a36e2' },
                      'delayed': { symbol: '🔥', color: '#f44245' },
                      'delayed-completed': { symbol: '✔', color: '#f44245' }
                    }[taskStatus];

                    if (iconConfig && beforeTodayWidth > 30) {
                      const iconSize = 14;
                      const iconX = start[0] + beforeTodayWidth - iconSize / 2 - 2;
                      const iconY = taskBarY + iconSize / 3;

                      // 白色圆圈背景
                      children.push({
                        type: 'circle',
                        shape: {
                          cx: iconX,
                          cy: iconY,
                          r: iconSize / 2
                        },
                        style: {
                          fill: '#fff',
                          stroke: iconConfig.color,
                          lineWidth: 1
                        },
                        z: 12
                      });

                      // 状态符号
                      children.push({
                        type: 'text',
                        style: {
                          x: iconX,
                          y: iconY,
                          text: iconConfig.symbol,
                          fill: iconConfig.color,
                          fontSize: 10,
                          fontWeight: 'bold',
                          textAlign: 'center',
                          textVerticalAlign: 'middle'
                        },
                        z: 13
                      });
                    }
                  }

                  // 评审任务图标
                  const dataItem = seriesData[categoryIndex];
                  if (dataItem?.reviewTask && beforeTodayWidth > 50) {
                    const reviewAttachments = dataItem?.reviewAttachments || [];
                    const hasAttachments = reviewAttachments.length > 0;
                    
                    let reviewIconConfig: { symbol: string; color: string };
                    if (hasAttachments) {
                      reviewIconConfig = { symbol: '⚠', color: '#57e434' };
                    } else if (taskStatus === 'in-progress') {
                      reviewIconConfig = { symbol: '⚠', color: '#2a36e2' };
                    } else {
                      reviewIconConfig = { symbol: '⚠', color: '#9ca3af' };
                    }

                    const iconSize = 14;
                    const iconX = start[0] + beforeTodayWidth - iconSize / 2 - 2 - 16;
                    const iconY = taskBarY + iconSize / 3;

                    // 白色圆圈背景
                    children.push({
                      type: 'circle',
                      shape: {
                        cx: iconX,
                        cy: iconY,
                        r: iconSize / 2
                      },
                      style: {
                        fill: '#fff',
                        stroke: reviewIconConfig.color,
                        lineWidth: 1
                      },
                      z: 12
                    });

                    // 评审任务符号
                    children.push({
                      type: 'text',
                      style: {
                        x: iconX,
                        y: iconY,
                        text: reviewIconConfig.symbol,
                        fill: reviewIconConfig.color,
                        fontSize: 10,
                        fontWeight: 'bold',
                        textAlign: 'center',
                        textVerticalAlign: 'middle'
                      },
                      z: 13
                    });
                  }
                } else {
                  // 普通任务的文本显示
                  children.push({
                    type: 'text',
                    style: {
                      x: rectShape.x + padding,
                      y: rectShape.y + rectShape.height / 2,
                      text: displayText,
                      fill: '#fff',
                      fontSize: fontSize,
                      fontWeight: '500',
                      textVerticalAlign: 'middle',
                      overflow: 'truncate',
                      width: Math.max(barWidth - padding * 2, 1)
                    },
                    z: 11
                  });

                  // 添加状态图标（右上角）
                  if (taskStatus && taskStatus !== 'not-started') {
                    const iconConfig = {
                      'completed': { symbol: '✔', color: '#57e434' },
                      'in-progress': { symbol: '🕐', color: '#2a36e2' },
                      'delayed': { symbol: '🔥', color: '#f44245' },
                      'delayed-completed': { symbol: '✔', color: '#f44245' }
                    }[taskStatus];

                    if (iconConfig) {
                      const iconSize = 14;
                      const iconX = rectShape.x + rectShape.width - iconSize / 2 - 2;
                      const iconY = rectShape.y + iconSize / 3;

                      // 白色圆圈背景
                      children.push({
                        type: 'circle',
                        shape: {
                          cx: iconX,
                          cy: iconY,
                          r: iconSize / 2
                        },
                        style: {
                          fill: '#fff',
                          stroke: iconConfig.color,
                          lineWidth: 1
                        },
                        z: 12
                      });

                      // 状态符号
                      children.push({
                        type: 'text',
                        style: {
                          x: iconX,
                          y: iconY,
                          text: iconConfig.symbol,
                          fill: iconConfig.color,
                          fontSize: 10,
                          fontWeight: 'bold',
                          textAlign: 'center',
                          textVerticalAlign: 'middle'
                        },
                        z: 13
                      });
                    }
                  }

                  // 添加评审任务图标（右上角状态图标左侧）
                  const dataItem = seriesData[categoryIndex];
                  if (dataItem?.reviewTask) {
                    const reviewAttachments = dataItem?.reviewAttachments || [];
                    const hasAttachments = reviewAttachments.length > 0;
                    
                    let reviewIconConfig: { symbol: string; color: string };
                    if (hasAttachments) {
                      // 已上传附件 - 显示绿色 attention icon
                      reviewIconConfig = { symbol: '⚠', color: '#57e434' };
                    } else if (taskStatus === 'in-progress') {
                      // 进行中 - 显示蓝色 attention icon
                      reviewIconConfig = { symbol: '⚠', color: '#2a36e2' };
                    } else {
                      // 未开始 - 显示灰色 attention icon
                      reviewIconConfig = { symbol: '⚠', color: '#9ca3af' };
                    }

                    const iconSize = 14;
                    const iconX = rectShape.x + rectShape.width - iconSize / 2 - 2 - 16;
                    const iconY = rectShape.y + iconSize / 3;

                    // 白色圆圈背景
                    children.push({
                      type: 'circle',
                      shape: {
                        cx: iconX,
                        cy: iconY,
                        r: iconSize / 2
                      },
                      style: {
                        fill: '#fff',
                        stroke: reviewIconConfig.color,
                        lineWidth: 1
                      },
                      z: 12
                    });

                    // 评审任务符号
                    children.push({
                      type: 'text',
                      style: {
                        x: iconX,
                        y: iconY,
                        text: reviewIconConfig.symbol,
                        fill: reviewIconConfig.color,
                        fontSize: 10,
                        fontWeight: 'bold',
                        textAlign: 'center',
                        textVerticalAlign: 'middle'
                      },
                      z: 13
                    });
                  }
                }

                // 对于 delayed 或 delayed-completed 状态任务，在原计划结束日期后添加红色透明斜线延长
                const isDelayedTask = taskStatus === 'delayed' || taskStatus === 'delayed-completed';
                const shouldExtendDelayedBar = taskStatus === 'delayed'
                  ? (todayTime > taskEndTime && todayX > end[0])
                  : (dataItem?.task?.actualCompletionDate && todayX > end[0]);

                if (isDelayedTask && shouldExtendDelayedBar) {
                  const delayedBarStartX = end[0];
                  const delayedBarEndX = todayX;
                  const delayedBarWidth = delayedBarEndX - delayedBarStartX;
                  
                  if (delayedBarWidth > 0) {
                    // 半透明背景
                    children.push({
                      type: 'rect',
                      shape: {
                        x: delayedBarStartX,
                        y: taskBarY,
                        width: delayedBarWidth,
                        height: barHeight,
                        r: 4
                      },
                      style: {
                        fill: 'rgba(244, 66, 69, 0.15)'
                      },
                      z: 9
                    });

                    // 绘制红色斜条纹
                    const stripeWidth = 4;
                    const stripeGap = 4;
                    const totalStripes = Math.ceil(delayedBarWidth / (stripeWidth + stripeGap)) + 1;
                    
                    for (let i = 0; i < totalStripes; i++) {
                      const offsetX = i * (stripeWidth + stripeGap);
                      const lineX1 = delayedBarStartX + offsetX;
                      const lineX2 = Math.min(lineX1 + stripeWidth, delayedBarStartX + delayedBarWidth);
                      
                      // 只绘制在延长区域内的斜线
                      if (lineX1 < delayedBarStartX + delayedBarWidth) {
                        children.push({
                          type: 'line',
                          shape: {
                            x1: lineX1,
                            y1: taskBarY,
                            x2: lineX2,
                            y2: taskBarY + barHeight
                          },
                          style: {
                            stroke: 'rgba(255, 255, 255, 0.5)',
                            lineWidth: 2,
                            lineCap: 'round'
                          },
                          z: 10
                        });
                      }
                    }

                    // 添加边框矩形
                    children.push({
                      type: 'rect',
                      shape: {
                        x: delayedBarStartX,
                        y: taskBarY,
                        width: delayedBarWidth,
                        height: barHeight,
                        r: 4
                      },
                      style: {
                        fill: 'transparent',
                        stroke: '#f44245',
                        lineWidth: 1
                      },
                      z: 12
                    });
                  }
                }
              }

              return rectShape && {
                type: 'group',
                children: children
              };
            },
            encode: {
              x: [0, 2],
              y: 1
            },
            data: seriesData
          },
          {
            type: 'line',
            markLine: {
              symbol: ['none', 'none'],
              label: { show: true },
              data: markLines,
              animation: false
            }
          }
        ]
      };

      // 保存当前的 dataZoom 状态
      try {
        const currentOption = chartInstance.current.getOption();
        if (currentOption?.dataZoom && currentOption.dataZoom[0]) {
          dataZoomStateRef.current = {
            start: currentOption.dataZoom[0].start,
            end: currentOption.dataZoom[0].end
          };
        }
      } catch (e) {
        // 首次渲染时可能没有 dataZoom
      }

      chartInstance.current.setOption(option, true);

      // 恢复 dataZoom 状态
      if (dataZoomStateRef.current) {
        chartInstance.current.setOption({
          dataZoom: [{
            start: dataZoomStateRef.current.start,
            end: dataZoomStateRef.current.end
          }]
        });
      }

      // 添加点击事件
      chartInstance.current.off('click');
      chartInstance.current.on('click', (params: any) => {
        if (params.componentType === 'series' && params.seriesType === 'custom') {
          const clickedData = seriesData[params.dataIndex];
          if (clickedData && clickedData.task && onTaskClick) {
            onTaskClick(clickedData.task);
          }
        }
      });

      // 计算 Today 标记线位置
      const updateTodayPosition = () => {
        if (!chartRef.current || !chartInstance.current || !todayMarkerRef.current) return;

        // 使用 ECharts 的 convertToPixel API 获取精确的像素位置
        const todayPoint = chartInstance.current.convertToPixel({ xAxisIndex: 0 }, todayTimeRef.current);
        if (todayPoint && typeof todayPoint === 'number') {
          // 获取图表容器的宽度和 grid 边距
          const chartWidth = chartRef.current.clientWidth;
          const leftMargin = 180; // 与 grid.left 一致
          const rightMargin = 40; // 与 grid.right 一致

          // 检查 Today 是否在可视区域内
          if (todayPoint < leftMargin || todayPoint > chartWidth - rightMargin) {
            // Today 在可视区域外，隐藏图标
            todayMarkerRef.current.style.display = 'none';
          } else {
            // Today 在可视区域内，显示图标并更新位置
            todayMarkerRef.current.style.display = 'block';
            todayMarkerRef.current.style.left = `${todayPoint}px`;
          }
        }
      };

      // 延迟更新位置，确保图表已渲染
      setTimeout(updateTodayPosition, 100);

      // 监听 dataZoom 变化，更新 Today 标记位置
      chartInstance.current.off('dataZoom');
      chartInstance.current.on('dataZoom', updateTodayPosition);

    }, [validTasks, vehicleNodes, onTaskClick]);

    return (
      <div style={{ width: '100%', height, position: 'relative', cursor: isDragging ? 'grabbing' : 'grab' }}>
        {/* Location 动画图标 - 位于 grid 上方 */}
        <div
          ref={todayMarkerRef}
          className="location-marker"
          style={{
            position: 'absolute',
            top: '-30px',  // 移到 grid 区域上方 30px
            left: '0px',
            transform: 'translateX(-50%)',
            zIndex: 20,
            pointerEvents: 'none'
          }}
        >
          <div className="location-loader" />
        </div>
        
        {validTasks.length === 0 && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: '#9ca3af', 
            border: '1px dashed #d1d5db', 
            borderRadius: '8px',
            zIndex: 10,
            background: 'white'
          }}>
            暂无任务数据，请先生成Timing
          </div>
        )}
        <div ref={chartRef} style={{ width: '100%', height }} />
        
        {/* CSS 动画样式 */}
        <style>{`
          .location-loader {
            width: 32px;
            height: 32px;
            display: block;
            position: relative;
          }
          
          .location-loader::after {
            content: '';
            width: 32px;
            height: 32px;
            left: 0;
            bottom: 0;
            position: absolute;
            border-radius: 50% 50% 0;
            border: 10px solid #f44245;
            transform: rotate(45deg) translate(0, 0);
            box-sizing: border-box;
            animation: animMarker 0.4s ease-in-out infinite alternate;
          }
          
          .location-loader::before {
            content: '';
            box-sizing: border-box;
            position: absolute;
            left: 0;
            right: 0;
            margin: auto;
            top: 140%;
            width: 16px;
            height: 3px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.2);
            animation: animShadow 0.4s ease-in-out infinite alternate;
          }
          
          @keyframes animMarker {
            0% {
              transform: rotate(45deg) translate(3px, 3px);
            }
            100% {
              transform: rotate(45deg) translate(-3px, -3px);
            }
          }
          
          @keyframes animShadow {
            0% {
              transform: scale(0.5);
            }
            100% {
              transform: scale(1);
            }
          }
        `}</style>
      </div>
    );
  }
);

GanttChart.displayName = 'GanttChart';
